import React from 'react';
import { Navigate } from 'react-router-dom';
import BgImage from 'src/assets/images/login-thumb.png';
import Logo from 'src/assets/images/new-logo-freya.svg';
import LogoTetrixx from 'src/assets/images/tetrixx.svg';
import { Authenticator, useAuthenticator, translations } from '@aws-amplify/ui-react';

import { makeStyles } from 'tss-react/mui';
import LogoFreya from 'src/assets/images/new-logo-freya-login.svg';

import { I18n } from 'aws-amplify/utils';

import '@aws-amplify/ui-react/styles.css';
import { signInWithRedirect } from 'aws-amplify/auth';

const formFields = {
  signIn: {
    username: {
      placeholder: 'Input Email',
      label: 'Email Address',
    },
    password: {
      placeholder: 'Input Password',
      label: 'Password',
    },
  },
  forgotPassword: {
    username: {
      placeholder: 'Input Email',
      label: 'Email Address',
    },
  },
  confirmResetPassword: {
    confirmation_code: {
      placeholder: 'Verification Code',
      label: 'Input Verification Code',
    },
    password: {
      placeholder: 'Input Password',
      label: 'Password',
    },
    confirm_password: {
      placeholder: 'Reinput Password',
    },
  },
};
const useStyles = makeStyles()((theme) => ({
  root: {
    '& [data-amplify-authenticator]': {
      fontWeight: '600',
    },
    '& .amplify-label': {
      color: '#36454F',
      fontSize: '14px',
      '@media (min-width: 1520px)': {
        fontSize: '1.125rem',
      },
    },
    '& [data-amplify-container]': {
      maxWidth: '390px!important',
      '@media (min-width: 1520px)': {
        maxWidth: '520px!important',
        width: '520px',
      },
    },
    '& .amplify-alert--error': {
      backgroundColor: '#FCE5E9',
      color: '#E72547',
    },
    '& .amplify-alert__body': {
      fontSize: '14px',
      '@media (min-width: 1520px)': {
        fontSize: '1.125rem',
      },
    },
    '& [data-amplify-router]': {
      backgroundColor: 'transparent',
      border: 'none',
      boxShadow: 'none',
      padding: 0,
    },
    '& [data-amplify-form]': {
      padding: '0!important',
    },
    '& .amplify-button[type="submit"]': {
      backgroundColor: theme.palette.primary.main,
      border: 'none',
      marginTop: '5px',
      borderRadius: '0.4375rem',
      color: '#ffff',
      fontWeight: '600!important',
      boxShadow: 'none',
      fontSize: '14px',
      '@media (min-width: 1520px)': {
        fontSize: '1.125rem',
        marginTop: '1rem',
      },
    },
    '& .amplify-input': {
      backgroundColor: '#F5F6FA',
      borderColor: '#BFBFBF',
      marginTop: '0',
      borderRadius: '10px',
      fontSize: '14px',
      '&:focus': {
        boxShadow: 'none',
      },
      '@media (min-width: 1520px)': {
        fontSize: '1.125rem',
      },
    },
    '& .amplify-field__show-password': {
      backgroundColor: '#F5F6FA',
      borderRadius: '0 10px 10px 0',
      borderColor: '#BFBFBF',
      color: '#9F9F9F',
      fontSize: '20px',
      paddingLeft: '10px',
      paddingRight: '10px',
    },
    '& .amplify-textfield': {
      marginBottom: '5px',
      '@media (min-width: 1520px)': {
        marginBottom: '1rem',
      },
    },
    '& .amplify-passwordfield .amplify-input': {
      borderRight: 'none',
    },
    '& .amplify-passwordfield .amplify-field__show-password': {
      borderLeft: 'none',
    },
    '& .amplify-alert': {
      borderRadius: '8px',
    },
    '& .amplify-alert .amplify-alert__dismiss': {
      padding: 0,
    },
    '.amplify-button--small': {
      fontSize: '14px',
      color: '#404040',
      border: 'none',
      boxShadow: 'none',
      '@media (min-width: 1520px)': {
        fontSize: '1.125rem',
      },
    },
    '.amplify-field__show-password': {
      boxShadow: 'none',
    },
    '[data-amplify-footer]': {
      paddingBottom: '2.25rem',
    },
  },
}));

I18n.putVocabularies(translations);
I18n.setLanguage('en');
I18n.putVocabulariesForLanguage('en', {
  'Sign in': 'Login',
  'Send code': 'Reset Password',
  'Back to Sign In': 'Back to Login',
});

const LoginPage: React.FC = () => {
  const { authStatus, toForgotPassword } = useAuthenticator((context) => [context.authStatus]);

  const { classes } = useStyles();

  if (authStatus === 'authenticated') {
    return <Navigate to='/' replace />;
  }

  return (
    <div className='w-screen h-screen flex items-center bg-white p-12'>
      <div className='w-1/2 h-full relative'>
        <div className='absolute inset-0'>
          <img src={BgImage} alt='' className='w-full h-full object-cover object-center rounded-24' />
        </div>
        <div className='relative z-10 flex flex-col h-full font-satoshi'>
          <div className='pt-[70px] xl:pt-[120px] pl-[60px]'>
            <div className='flex flex-col text-[#848F88] text-[56px] xl:text-[72px] leading-[72px] xl:leading-[85px] tracking-tight'>
              <p>
                Automating <strong className='text-white font-bold'>Complexity,</strong>
              </p>
              <p>
                Delivering <strong className='text-white font-bold'>Clarity.</strong>
              </p>
            </div>
            <div className='mt-32 xl:mt-[60px] flex items-center gap-12'>
              <span className='text-base text-[#B5BAB7] text-16 xl:text-[22px]'>Powered by </span>
              <img src={Logo} alt='Freya' className='h-24 xl:h-36' />
            </div>
          </div>
          <div className='mt-auto pt-16 pl-20 pb-20'>
            <div className='pl-6 py-6 pr-12 rounded-12 inline-flex items-center gap-10 bg-white/10 border border-white/20'>
              <div className='w-24 xl:w-32 h-24 xl:h-32 bg-white rounded-8 flex items-center justify-center'>
                <img src={LogoTetrixx} alt='' className='w-14 xl:w-22' />
              </div>
              <strong className='text-14 xl:text-18 text-white font-bold'>Tetrixx</strong>
            </div>
          </div>
        </div>
      </div>
      <div className='w-1/2 h-full relative'>
        <div className='w-full h-full relative z-10 flex flex-col items-center justify-center'>
          <div className='w-full flex-1 flex items-center justify-center pt-20'>
            <div className='w-full flex flex-col max-w-[400px] xl:max-w-[483px]'>
              <div className='mb-10'>
                <img src={LogoFreya} alt='' className='h-48' />
              </div>
              <Authenticator
                hideSignUp
                className={classes.root}
                formFields={formFields}
                components={{
                  SignIn: {
                    Header() {
                      return (
                        <div className='text-28 xl:text-36 leading-36 xl:leading-48 mt-48 mb-32 pb-32 border-b border-neutral-20'>
                          <p className='font-light font-satoshi'>Access your</p>
                          <strong className='font-bold font-satoshi'>
                            Transportation &<br />
                            Logistics Intelligence
                          </strong>
                        </div>
                      );
                    },
                    Footer() {
                      return (
                        <div className='flex items-center pb-30 mt-12'>
                          <button
                            type='button'
                            className='text-14 xl:text-18 underline text-primary'
                            onClick={async () => {
                              try {
                                await signInWithRedirect({ provider: { custom: 'OktaOIDP' } });
                              } catch (error) {
                                // Handle any errors that may occur during the SSO process
                                console.error('Error during federated sign-in:', error);
                              }
                            }}
                          >
                            Login via SSO
                          </button>

                          <div className='ml-auto pl-16'>
                            <button type='button' className='text-14 xl:text-18 underline' onClick={() => toForgotPassword()}>
                              Forgot password?
                            </button>
                          </div>
                        </div>
                      );
                    },
                  },
                  ForgotPassword: {
                    Header() {
                      return (
                        <div className='pt-16'>
                          <div className='pb-32 mb-16 border-b border-neutral-40 flex flex-col'>
                            <h2 className='text-28 xl:text-36 leading-36 xl:leading-48 font-bold mb-4 font-satoshi'>Reset Password</h2>
                            <p className='text-14 xl:text-18 text-neutral-70'>We’ll send you the reset password link to your email</p>
                          </div>
                        </div>
                      );
                    },
                  },
                  ConfirmResetPassword: {
                    Header() {
                      return (
                        <div className='pt-16'>
                          <div className='pb-24 border-b border-neutral-40 flex flex-col items-left'>
                            <h2 className='text-28 xl:text-36 leading-36 xl:leading-48 font-bold mb-4'>Reset Password</h2>
                            <p className='text-14 xl:text-18 text-neutral-70 text-left'>Please check your email and input the verification code below</p>
                          </div>
                        </div>
                      );
                    },
                  },
                }}
              />
            </div>
          </div>

          <div className='absolute bottom-5 left-0 right-0'>
            <p className='text-neutral-60 text-12 xl:text-14 text-center'>&copy; Tetrixx {new Date().getFullYear()}. All Rights Reserved</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
